import React, { useState, useEffect } from 'react';
import { Paper, Tab, Tabs, Box, CircularProgress } from '@material-ui/core';
import { useNotify, useRefresh, Title } from 'react-admin';
import { serverApiUrl } from '../../../config/constants';
import httpClient from '../../../core/httpClient';

import SplashScreenTab from './tabs/SplashScreenTab';
import TutorialTab from './tabs/TutorialTab';
import LevelSelectorTab from './tabs/LevelSelectorTab';
import LoadingScreenTab from './tabs/LoadingScreenTab';
import LevelsTab from './tabs/LevelsTab';
import LogosTab from './tabs/LogosTab';
import WordsTab from './tabs/WordsTab';

const LEVEL_COUNT = 8;
const WORDS_PER_LEVEL = 15;

const WordFinderConfigEdit = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const notify = useNotify();
  const refresh = useRefresh();

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const url = `${serverApiUrl}/admin/word-finder-config`;
      const response = await httpClient(url, { method: 'GET' });
      setConfig(response.json);
    } catch (error) {
      notify('Error loading configuration', 'warning');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const convertUndefinedToNull = (obj) => {
    if (obj === null || obj === undefined) return null;
    if (Array.isArray(obj)) {
      return obj.map((item) => convertUndefinedToNull(item));
    }
    if (typeof obj === 'object') {
      return Object.keys(obj).reduce((acc, key) => {
        acc[key] = convertUndefinedToNull(obj[key]);
        return acc;
      }, {});
    }
    return obj;
  };

  const handleSave = async (updatedData) => {
    const sanitizedData = convertUndefinedToNull(updatedData);
    try {
      const url = `${serverApiUrl}/admin/word-finder-config`;
      const response = await httpClient(url, {
        method: 'PUT',
        body: JSON.stringify(sanitizedData),
      });
      setConfig(response.json);
      notify('Configuration saved successfully');
      refresh();
    } catch (error) {
      notify('Error saving configuration', 'warning');
    }
  };

  const handleChange = (_, newValue) => {
    setCurrentTab(newValue);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const tabs = [
    <SplashScreenTab config={config} onSave={handleSave} />,
    <TutorialTab config={config} onSave={handleSave} />,
    <LevelSelectorTab config={config} onSave={handleSave} />,
    <LoadingScreenTab config={config} onSave={handleSave} />,
    <LevelsTab config={config} onSave={handleSave} levelCount={LEVEL_COUNT} />,
    <LogosTab config={config} onSave={handleSave} />,
    <WordsTab config={config} onSave={handleSave} levelCount={LEVEL_COUNT} wordsPerLevel={WORDS_PER_LEVEL} />,
  ];

  return (
    <>
      <Title title="Word Finder Configuration" />
      <Paper>
        <Tabs
          value={currentTab}
          onChange={handleChange}
          indicatorColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Splash Screen" />
          <Tab label="Tutorial" />
          <Tab label="Level Selector" />
          <Tab label="Loading Screen" />
          <Tab label="Levels (8)" />
          <Tab label="Logos" />
          <Tab label="Words" />
        </Tabs>
      </Paper>
      <Box mt={2}>{tabs[currentTab]}</Box>
    </>
  );
};

export default WordFinderConfigEdit;
